import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { SCENE } from '../constants.js'

// ============================================================================
//  Stage — renderer, scena, dwie kamery (3D perspektywiczna / 2D ortograficzna),
//  sterowanie i środowisko odbić. Przełączanie widoku przez setView('2d'|'3d'),
//  auto-kadrowanie do rozmiaru boiska przez frameCourt(bounds).
// ============================================================================

export class Stage {
  constructor(container) {
    this.container = container

    // --- Renderer ---
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
      alpha: true, // przezroczyste tło — widać gradient CSS pod kanwą
    })
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
    const { width, height, aspect } = this._size()
    this.renderer.setSize(width, height)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    // NeutralToneMapping (Khronos PBR Neutral) zamiast ACES Filmic: ACES odsyca
    // i ściąga barwy ku szarości („blade" kolory), Neutral zachowuje nasycenie
    // i odcień, kompresując tylko jasne szczyty — wybór pod konfigurator produktu.
    this.renderer.toneMapping = THREE.NeutralToneMapping
    this.renderer.toneMappingExposure = SCENE.DAY_EXPOSURE
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    container.appendChild(this.renderer.domElement)

    // --- Scena ---
    this.scene = new THREE.Scene()

    // --- Kamera 3D (izometryczny kadr startowy) ---
    // near/far ciasno wokół realnej sceny (~15 m, maxDistance 40): stosunek 0.3/80
    // zamiast 0.1/300 zwielokrotnia precyzję bufora głębi i tłumi z-fighting
    // (m.in. migotanie nieprzezroczystych elementów widocznych przez szybę tablicy).
    this.persp = new THREE.PerspectiveCamera(45, aspect, 0.3, 80)
    this.persp.position.set(6.0, 4.3, 8.0)

    // --- Kamera 2D (ortograficzna, z góry; +Z „w dół" ekranu) ---
    this.ortho = new THREE.OrthographicCamera(-10 * aspect, 10 * aspect, 10, -10, 0.1, 300)
    this.ortho.position.set(0, 50, 3)
    this.ortho.up.set(0, 0, -1)
    this.ortho.lookAt(0, 0, 3)

    this.view = '3d'
    this.camera = this.persp

    // --- Środowisko odbić (PBR) bez plików zewnętrznych ---
    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    this.scene.environment = this.envMap
    pmrem.dispose()

    // --- Sterowanie 3D (orbit + zoom + auto-obrót) ---
    this.controls3d = new OrbitControls(this.persp, this.renderer.domElement)
    this.controls3d.enableDamping = true
    this.controls3d.dampingFactor = 0.05
    this.controls3d.target.set(0, 0.6, 3)
    this.controls3d.minDistance = 1.5
    this.controls3d.maxDistance = 40
    this.controls3d.maxPolarAngle = Math.PI * 0.495 // nie schodzimy pod parkiet
    this.controls3d.autoRotate = true
    this.controls3d.autoRotateSpeed = 0.45

    // --- Sterowanie 2D (pan + zoom, bez obrotu) ---
    this.controls2d = new OrbitControls(this.ortho, this.renderer.domElement)
    this.controls2d.enableDamping = true
    this.controls2d.dampingFactor = 0.05
    this.controls2d.enableRotate = false
    this.controls2d.target.set(0, 0, 3)
    this.controls2d.enabled = false

    // Auto-obrót: pauza podczas interakcji, powrót po bezczynności.
    // _rotationLocked: kłódka z UI trzyma obrót zatrzymany na stałe.
    this._idleTimer = 0
    this._userActive = false
    this._rotationLocked = false
    this.controls3d.addEventListener('start', () => {
      this._userActive = true
      this.controls3d.autoRotate = false
    })
    this.controls3d.addEventListener('end', () => {
      this._userActive = false
      this._idleTimer = 0
    })

    // Ostatni kadr (do przeliczeń orto przy resize).
    this._frame = { width: 8, length: 8, centerZ: 3 }

    // ResizeObserver zamiast resize okna: łapie też zmianę układu strony
    // (pojawienie się sidebara, przejście desktop↔mobile).
    this._ro = new ResizeObserver(() => this._onResize())
    this._ro.observe(container)
  }

  // Przełącza aktywną kamerę i tryb sterowania.
  setView(view) {
    this.view = view
    if (view === '2d') {
      this.camera = this.ortho
      this.controls3d.enabled = false
      this.controls3d.autoRotate = false
      this.controls2d.enabled = true
    } else {
      this.camera = this.persp
      this.controls2d.enabled = false
      this.controls3d.enabled = true
    }
  }

  // Rozmiar kontenera sceny (min 1 px — zabezpieczenie przed dzieleniem przez zero).
  _size() {
    const width = Math.max(1, this.container.clientWidth)
    const height = Math.max(1, this.container.clientHeight)
    return { width, height, aspect: width / height }
  }

  // Auto-kadrowanie do rozmiaru boiska (po starcie i po każdej zmianie wymiarów).
  frameCourt(bounds) {
    this._frame = { width: bounds.width, length: bounds.length, centerZ: bounds.centerZ }
    const cx = 0
    const cz = bounds.centerZ
    const radius = 0.5 * Math.hypot(bounds.width, bounds.length)
    const aspect = this._size().aspect

    // 3D: target na środek płyty, odległość izometryczna mieszcząca całość.
    this.controls3d.target.set(cx, 0.4, cz)
    const fov = (this.persp.fov * Math.PI) / 180
    const fitH = radius / Math.tan(fov / 2)
    const fitW = fitH / Math.min(aspect, 1)
    // Margines wokół kadru: na mobile mocno ciaśniej (boisko wypełnia wąski
    // ekran; otaczająca „kula" płaskiego boiska zostawia dużo pustki, stąd
    // agresywny mnożnik), na desktopie z zapasem na pełny izometryczny widok kosza.
    const margin = window.matchMedia('(max-width: 768px)').matches ? 0.6 : 0.95
    const dist = margin * Math.max(fitH, fitW)
    const dir = new THREE.Vector3(0.75, 0.62, 1).normalize()
    this.persp.position.copy(dir.multiplyScalar(dist)).add(new THREE.Vector3(cx, 0.4, cz))
    this.persp.updateProjectionMatrix()
    this.controls3d.update()

    // 2D: frustum orto wokół boiska, kamera nad środkiem.
    this._applyOrtho()
    this.ortho.position.set(cx, 50, cz)
    this.ortho.up.set(0, 0, -1)
    this.ortho.lookAt(cx, 0, cz)
    this.ortho.zoom = 1
    this.ortho.updateProjectionMatrix()
    this.controls2d.target.set(cx, 0, cz)
    this.controls2d.update()
  }

  // Aktualizacja granic bez ruszania kamer: zapamiętuje nowy rozmiar płyty i
  // przelicza frustum 2D, by boisko mieściło się w kadrze ortograficznym — ale
  // NIE rusza pozycji/celu/zoomu żadnej kamery (zachowuje obrót i przybliżenie
  // ustawione przez użytkownika po pierwszym auto-kadrze).
  refit(bounds) {
    this._frame = { width: bounds.width, length: bounds.length, centerZ: bounds.centerZ }
    this._applyOrtho()
  }

  // Ustawia frustum kamery orto z ostatniego kadru + bieżącego aspektu.
  _applyOrtho() {
    const { aspect } = this._size()
    const margin = 1.12
    let halfW = (this._frame.width / 2) * margin
    let halfH = (this._frame.length / 2) * margin
    if (halfW / aspect > halfH) halfH = halfW / aspect
    else halfW = halfH * aspect
    this.ortho.left = -halfW
    this.ortho.right = halfW
    this.ortho.top = halfH
    this.ortho.bottom = -halfH
    this.ortho.updateProjectionMatrix()
  }

  _onResize() {
    const { width, height, aspect } = this._size()
    this.persp.aspect = aspect
    this.persp.updateProjectionMatrix()
    this._applyOrtho()
    this.renderer.setSize(width, height)
  }

  setExposure(value) {
    this.renderer.toneMappingExposure = value
  }

  // Efektywny dystans kamery do gaszenia faktury — wspólna miara dla obu widoków.
  // 3D: realny dystans kamery do celu orbity. 2D orto: kamera stoi nieruchomo, więc
  // przeliczamy widoczną wysokość świata (maleje z rosnącym zoomem) na ekwiwalentny
  // dystans perspektywiczny (ile metrów dzieliłoby kamerę 3D, by pokazać tyle samo
  // pola w pionie) — dzięki temu te same progi TILE_TEX_NEAR/FAR gaszą fakturę i przy
  // oddalaniu orto, i przy oddalaniu w 3D.
  textureCameraDistance() {
    if (this.view === '3d') return this.persp.position.distanceTo(this.controls3d.target)
    const worldH = (this.ortho.top - this.ortho.bottom) / this.ortho.zoom
    return worldH / (2 * Math.tan((this.persp.fov * Math.PI / 180) / 2))
  }

  render() {
    this.renderer.render(this.scene, this.camera)
  }

  // Kłódka z UI: zatrzymuje automatyczny obrót sceny i blokuje jego wznowienie
  // po bezczynności. Ręczne obracanie (przeciąganie) pozostaje możliwe.
  setRotationLock(locked) {
    this._rotationLocked = locked
    if (locked) this.controls3d.autoRotate = false
    this._idleTimer = 0
  }

  update(dt) {
    if (this.view === '3d') {
      // Po ~2,5 s bezczynności wznawiamy powolny auto-obrót (o ile kłódka nie blokuje).
      if (!this._userActive && !this._rotationLocked && !this.controls3d.autoRotate) {
        this._idleTimer += dt
        if (this._idleTimer > 2.5) this.controls3d.autoRotate = true
      }
      this.controls3d.update()
    } else {
      this.controls2d.update()
    }
  }
}
